import { useBreakpoint } from '@dropbox/dig-components/hooks';
import { Text } from '@dropbox/dig-components/typography';
import { Split, Stack } from '@dropbox/dig-foundations';
import { UIIcon } from '@dropbox/dig-icons';
import { ChevronRightLine } from '@dropbox/dig-icons/assets';
import classnames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import ToolbarZoomControls from 'signer-app/parts/toolbar-zoom-controls';
import { NextButton } from 'signer-app/signer-experience/next-button';
import { PreviousButton } from 'signer-app/signer-experience/previous-button';
import { AttachmentsButton } from 'signer-app/signer-experience/signer-toolbar/attachments-button';
import messages from 'signer-app/signer-experience/signer-toolbar/messages';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';
import { useBundleProps } from 'signer-app/bundle-props';
import styles from './signer-toolbar.module.css';
import { ContinueButton } from '../action-buttons/continue-button';
import { useSignerContext } from '../signer-experience';
import { PreviousButton as PreviousButtonV2 } from '../action-buttons/previous-button';
import { NextButton as NextButtonV2 } from '../action-buttons/next-button';
import {
  useFieldsContext,
  useSignatureRequestContext,
} from '../../signature-request/context';
import { FieldCountIndicator } from './field-count-indicator';
import { SignStepToolbarLayout } from './sign-step-toolbar-layout';
import { AccessibleDocumentToggle } from 'signer-app/signer-signature-document/accessible-document-toggle';

interface Props {
  toolbarMenu: React.ReactNode;
  requiredFieldsLeft: number;
}

function ClassicSignStepToolbar({ toolbarMenu, requiredFieldsLeft }: Props) {
  const intl = useIntl();
  const { requiredAttachmentsLeft, hasAttachments } = useAttachmentsInfo();
  const { allValid } = useSignerContext();
  const { hasCustomStyles } = useBundleProps();

  const { small: displaySmall, medium: displayMedium } = useBreakpoint();

  // we need to check if we should show the Continue button even on lower breakpoints
  // this is a blatant copypaste from next-button.tsx but I'm hoping we will refactor all of this soon
  const shouldShowFullContinueButton =
    (!hasAttachments || requiredAttachmentsLeft === 0) &&
    requiredFieldsLeft === 0 &&
    allValid;

  return (
    <SignStepToolbarLayout>
      <Split gap="Micro Small" alignY="center">
        <Split.Item>{toolbarMenu}</Split.Item>
        <Split.Item>
          <Stack gap="Micro Small">
            <Text
              variant="paragraph"
              size={'xsmall'}
              color={hasCustomStyles ? 'subtle' : 'standard'}
            >
              {requiredAttachmentsLeft
                ? intl.formatMessage(
                    messages.requiredFieldsAndAttachmentsLeft,
                    {
                      requiredFieldsLeft,
                      requiredAttachmentsLeft,
                    },
                  )
                : intl.formatMessage(messages.requiredFieldsLeft, {
                    requiredFields: requiredFieldsLeft,
                  })}
            </Text>
          </Stack>
        </Split.Item>
      </Split>

      <Split gap="Micro Large" alignY="center">
        <Split.Item>
          <ToolbarZoomControls />
        </Split.Item>
        <Split.Item>
          <Split gap="Micro Small" alignY="center">
            <Split.Item
              className={displaySmall ? undefined : styles.smallIconButton}
            >
              <PreviousButton
                size="large"
                variant={hasCustomStyles ? 'outline' : 'opacity'}
              />
            </Split.Item>
            {displayMedium || shouldShowFullContinueButton ? (
              <Split.Item>
                <NextButton size="large" />
              </Split.Item>
            ) : (
              <Split.Item
                className={classnames({
                  [styles.smallIconButton]: !displaySmall,
                })}
              >
                <WhiteLabelThemeProvider mode="bright">
                  <NextButton icon size="large">
                    <UIIcon src={ChevronRightLine} />
                  </NextButton>
                </WhiteLabelThemeProvider>
              </Split.Item>
            )}
          </Split>
        </Split.Item>
      </Split>
    </SignStepToolbarLayout>
  );
}

function MobileReadySignStepToolbar({
  toolbarMenu,
  requiredFieldsLeft,
}: Props) {
  const { hasAttachments } = useAttachmentsInfo();
  const { isMobileReady, hideFormViewToggle } = useSignerContext();
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  const { small: displaySmall } = useBreakpoint();

  const isShowingAttachmentsButton = hasAttachments && isFormViewAvailable;

  const { selectedFieldIds } = useSignatureRequestContext();
  const fields = useFieldsContext();
  const [activeField] = selectedFieldIds;
  const currentFieldIndex = fields.findIndex((f) => f.id === activeField);
  const shouldShowPrevButton = currentFieldIndex !== 0;

  return (
    <SignStepToolbarLayout>
      <Split gap="Micro Small" alignY="center">
        <Split.Item>{toolbarMenu}</Split.Item>
        <Split.Item>
          <ToolbarZoomControls variant="outline" />
        </Split.Item>
        {isFormViewAvailable && (
          <Split.Item>
            <AccessibleDocumentToggle />
          </Split.Item>
        )}
      </Split>

      <Split
        gap={{
          small: 'Micro Large',
          xsmall: 'Micro Small',
        }}
        alignY="center"
      >
        <Split.Item>
          <FieldCountIndicator requiredFieldsLeft={requiredFieldsLeft} />
        </Split.Item>
        {isShowingAttachmentsButton && (
          <Split.Item>
            <AttachmentsButton />
          </Split.Item>
        )}

        <Split.Item>
          <Split gap="Micro Small" alignY="center">
            {shouldShowPrevButton && (
              <Split.Item>
                <PreviousButtonV2 />
              </Split.Item>
            )}
            <Split.Item>
              <NextButtonV2 icon={!displaySmall}>
                <UIIcon src={ChevronRightLine} />
              </NextButtonV2>
            </Split.Item>
          </Split>
        </Split.Item>
        <Split.Item>
          <ContinueButton />
        </Split.Item>
      </Split>
    </SignStepToolbarLayout>
  );
}

export function SignStepToolbar({ toolbarMenu, requiredFieldsLeft }: Props) {
  const { isMobileReady, hideFormViewToggle } = useSignerContext();
  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  if (isFormViewAvailable) {
    return (
      <MobileReadySignStepToolbar
        toolbarMenu={toolbarMenu}
        requiredFieldsLeft={requiredFieldsLeft}
      />
    );
  }

  return (
    <ClassicSignStepToolbar
      toolbarMenu={toolbarMenu}
      requiredFieldsLeft={requiredFieldsLeft}
    />
  );
}
